/**
 * m3 echo Frontend - Changelog Tab
 *
 * Version history display (moved from Settings).
 */

import React from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';

export const ChangelogTab: React.FC = () => {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <HistoryIcon sx={{ mr: 1, fontSize: 28 }} />
        <Typography variant="h6">
          Versionshistorie
        </Typography>
      </Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
        Alle Änderungen und neuen Features in m3 echo
      </Typography>

      {/* v5.4.0 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderLeft: '4px solid', borderColor: 'primary.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 5.4.0
          </Typography>
          <Chip label="Aktuell" color="primary" size="small" sx={{ ml: 2 }} />
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Oktober 2026 — Zentrale Modellaufstellung und Gemini 3
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Modelle zentral festgelegt
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Nutzer wählen bei Projekten, Tests und Autopiloten keine Modelle mehr aus</li>
          <li>Der Administrator legt die Modellaufstellung zentral fest; sie gilt für alle Projekte</li>
          <li>Autopiloten und manuelle Tests verwenden die Aufstellung ab dem nächsten Lauf</li>
          <li>Re-Tests nutzen die aktuelle Aufstellung; Prompts, Platzhalter und Suchbegriffe bleiben identisch</li>
          <li>Ergebnisse früherer Modelle bleiben in der Auswertung sichtbar</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Neue Modelle
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Benchmark-Aufstellung: Gemini 3.6 Flash, Gemini 3.1 Pro (Preview) und GPT-5.6 Luna</li>
          <li>Analyse-Layer 2 und 4 auf Gemini 3.6 Flash, Layer 5 und 6 auf Gemini 3.1 Pro (Preview), Layer 2.1 und Chatbot auf Gemini 3.5 Flash-Lite</li>
          <li>Kosten der neuen Modelle werden vollständig erfasst, einschließlich der internen Denk-Tokens</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Modellpflege (Admin)
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Admin-Tab „KI-Modelle" heißt jetzt „Modellpflege"</li>
          <li>Kostenvorschau für die Aufstellung (Schätzung) und Stabilitätsansicht der letzten 7 Tage</li>
          <li>„Websuche testen" prüft das Grounding eines Modells</li>
          <li>Deaktivieren statt Löschen für bereits verwendete Modelle</li>
          <li>Fallback-Modell je Analyse-Layer (Admin → Layers): Schlägt das primäre Modell fehl, wird der Aufruf einmal mit dem Fallback wiederholt</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          LLM Score je Anbieter
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Der LLM Score wird erst je Anbieter gemittelt, dann über die Anbieter: Jeder Anbieter zählt gleich, egal wie viele seiner Modelle in der Aufstellung sind</li>
          <li>Fehlgeschlagene Ergebnisse (API-Fehler, Timeouts, nicht verfügbare Modelle) zählen nicht mehr als 0; ein Test ohne gültiges Ergebnis ist „nicht auswertbar“</li>
          <li>Die Nennungsrate folgt derselben Regel; Projekt- und Mandantenrate sind der Mittelwert der abgeschlossenen Tests</li>
          <li>Alle bisherigen Scores wurden rückwirkend nach der neuen Regel neu berechnet</li>
        </Typography>
      </Paper>

      {/* v5.2.0 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 5.2.0
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Job Integrity Hardening, Kostentracking, Job Reliability, Sicherheits-Fixes
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Prompt-Snapshotting & Soft-Delete
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Prompt-Content wird bei Job-Erstellung als Snapshot gespeichert</li>
          <li>Retests verwenden immer den ursprünglichen Prompt-Text</li>
          <li>Prompts werden soft-deleted: historische Jobs und Ergebnisse bleiben erhalten</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Sicherheit & Validierung
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Cross-Tenant-Prompt-Zugriff auch für Admins blockiert</li>
          <li>Retests prüfen Modell-Verfügbarkeit vor Ausführung</li>
          <li>Verbesserte Fehlerbehandlung: saubere 4xx statt 500-Fehler</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Kostentracking
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Versionierte Preisgestaltung mit historischer Genauigkeit</li>
          <li>Per-Job-Kosten-Breakdown und Budget-Alerts pro Mandant</li>
          <li>Pricing-Section in KI-Modelle Admin-Tab</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Job Reliability & Observability
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Watchdog-Service mit Stuck-Job-Erkennung und Email-Digest</li>
          <li>Notification Bell und Admin Ops Tab</li>
          <li>Error-Classifier für intelligente Retry-Entscheidungen</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Infrastruktur
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Firecrawl ersetzt Zyte als Web Scraper</li>
          <li>Maintenance-Mode mit Admin-Toggle</li>
          <li>Pydantic V2 Migration, timezone-aware UTC</li>
          <li>Weekly Email Reports</li>
        </Typography>
      </Paper>

      {/* v5.1.0 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 5.1.0
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Billing-Modul-System, Scoring V2, Cutoff Tracking, Echtzeit-Fortschritt, Interaktive Heatmap
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Billing-Modul-System
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Neues modulares Abrechnungssystem: Quota, Model, Feature und Custom Module</li>
          <li>Flexible Zeitsteuerung: Aktiv- und Abrechnungszeitraum unabhängig konfigurierbar</li>
          <li>Pro-Mandant-Rabatte und Kulanzzeit (Grace Period) nach Modulablauf</li>
          <li>Admin Billing-Seite mit drei Tabs: Module, Mandanten-Zuweisungen, EBIT-Dashboard</li>
          <li>EBIT-Tab: Monatliche Einnahmen vs. Token-Kosten-Analyse</li>
          <li>User API: Mandanten können eigene Billing-Konfiguration einsehen</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Cutoff Detail-Verbesserungen
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Vollständiger Originalinhalt statt nur erste 500 Zeichen</li>
          <li>Mandantenname statt Tenant-ID im Detail-Dialog</li>
          <li>Vergrößerter Content-Bereich im Detail-Dialog</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Cutoff Logging System
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Neues Cutoff-Tracking: Automatische Protokollierung aller Textkürzungen im System</li>
          <li>Admin Cutoffs-Tab unter Tools mit Tabelle, Filtern und Detailansicht</li>
          <li>Logging an 10 Kürzungspunkten: L3, L5, L6, Zyte, Brave, OpenAI Grounding</li>
          <li>Cutoff-Limits angepasst (L3, L5, L6, Zyte, Grounding, Prompt-Validierung)</li>
          <li>CSV-Export: Antwortkürzung entfernt</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Echtzeit-Fortschrittsanzeige
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Dashboard: Pie-Chart-Kreise zeigen Testfortschritt als ausfüllender Kreis</li>
          <li>LLM Score: Granularer Fortschritt (3 Schritte pro Modell) mit Modell-Anzeigenamen</li>
          <li>Optimierung: 4 klar benannte Schritte (Scraping, Daten, KI-Analyse, Speichern)</li>
          <li>Zuverlässiger DB-basierter Fortschritt als Fallback zu WebSocket</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Interaktive Heatmap & Projektdetails
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Klick auf Heatmap-Zelle scrollt zum Test und öffnet Job-Details</li>
          <li>Durchschnittliche Punktzahl basiert jetzt auf den letzten 10 Tests</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Bug Fixes
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Layer 5 Grounding Timeout und Optimierungs-Workflow verbessert</li>
          <li>CSV-Export paginiert jetzt durch alle Ergebnisse</li>
          <li>React Error #31 bei Backend-Daten behoben (3 Stellen)</li>
          <li>Absturz bei V2-Optimierung mit V1-Historie behoben</li>
          <li>Produktions-Absturz bei V1-Tests und Prompt-Qualitäts-Falschpositive behoben</li>
          <li>CSV-Export-Button im Insights-Tab hinzugefügt</li>
          <li>UI-Polish: Radar-Chart-Labels, einklappbare Test History</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Neues Bewertungssystem (Scoring V2)
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>7 gewichtete AI-Visibility-Kategorien ersetzen die 6 branchenspezifischen Kriterien</li>
          <li>K1 Entity-Klarheit (20%), K2 Zitierfähigkeit (20%), K3 Reputation (20%), K4 USP (12%), K5 Lokale Relevanz (10%), K6 Drittquellen (13%), K7 Technische Signale (5%)</li>
          <li>Gewichteter Gesamt-Score statt einfachem Durchschnitt</li>
          <li>Sub-Signale pro Kategorie mit individuellen Scores und Befunden</li>
          <li>Priorisierte Empfehlungen mit Aufwands-Einschätzung</li>
          <li>Top-Recommendations-Card mit den wichtigsten Empfehlungen</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Abwärtskompatibilität
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Info-Banner in der Timeline bei gemischten V1/V2-Daten</li>
          <li>Pearson-Korrelation nur noch auf V2-Tests (V1-Scores nicht vergleichbar)</li>
          <li>V1-Tests werden weiterhin korrekt in der Historie angezeigt</li>
        </Typography>
      </Paper>

      {/* v5.0.0 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 5.0.0
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Layer 6 Entwurf-Generierung, Zyte Web Scraping, Dynamic Model Registry, Custom Autopilot, Insights & Debug Log API
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Layer 6: Entwurf-Generierung
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>"Wizard"-Button neben jeder Optimierungsempfehlung</li>
          <li>KI-gestützte Entwurf-Generierung mit Grounding (besucht die Website)</li>
          <li>Detaillierte, umsetzbare Entwürfe für Optimierungsempfehlungen</li>
          <li>Entwürfe werden gespeichert und bei erneutem Klick sofort angezeigt</li>
          <li>Layer 6 Modell-Konfiguration im Admin-Bereich</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Layer 5: Zyte Web Scraping
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Zyte API-Integration für Website-Content-Extraktion in der Optimierungsanalyse</li>
          <li>Automatische Extraktion von Homepage-Inhalten für die KI-Analyse</li>
          <li>Browser-Rendering-Modus für JavaScript-lastige Websites</li>
          <li>Response-basiertes Preismodell mit Token-Tracking</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Dynamic AI Model Registry
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Neue ai_models Datenbanktabelle für dynamische Modell-Verwaltung</li>
          <li>Admin Models-Tab für Modell-Konfiguration</li>
          <li>GPT-5.2 und GPT-5.2-mini Unterstützung</li>
          <li>Modelle werden jetzt aus der Datenbank statt aus Konstanten geladen</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Haupttool Mobile-Optimierung
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Responsive Layout mit Hamburger-Menü und einklappbarer Sidebar (~30 Dateien)</li>
          <li>Card-basierte Ansichten für Dashboard und Projektdetails auf Mobile</li>
          <li>Heatmap in ausklappbarem Accordion auf Mobile</li>
          <li>Form-lastige Dialoge werden fullScreen auf Mobile (~15 Dialoge)</li>
          <li>Responsive Charts, Statistik-Grids und Admin-Tabellen</li>
          <li>Chatbot-Widget responsive Größe und Position</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Demo Mobile-Optimierung
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Responsive Layout für die Klickdemo unter /demo</li>
          <li>Hamburger-Menü und ausklappbare Sidebar auf Mobile</li>
          <li>Card-Ansichten statt Tabellen auf kleinen Bildschirmen</li>
          <li>Fullscreen-Dialoge auf Mobile-Geräten</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Fixes & Verbesserungen
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Insights Projektfilter zeigt jetzt alle Projekte für Admins</li>
          <li>Zentrale Audit-Logging-Funktion mit PII-Hashing</li>
          <li>Neue Migrationen für AI Models, Layer 5 Scraper und Layer 6 Drafts</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Custom Autopilot & Tools-Seite
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Neue "Tools"-Seite in der Sidebar für erweiterte Analyse-Werkzeuge</li>
          <li>Custom Autopilot – frei konfigurierbare Autopiloten mit eigenem Suchbegriff</li>
          <li>Eye-Icon zum Anzeigen von Job-Details für Custom-Autopilot-Runs</li>
          <li>Scheduling und Zeitzone-Anzeige für Custom Autopilots</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Grounding Benchmark
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Neues Tool: LLM-Antworten mit und ohne Web-Suche vergleichen (Gemini Grounding)</li>
          <li>Google Search Grounding für Gemini 2.5 Modelle</li>
          <li>Quellen-Anzeige mit Domain-Gruppierung</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Gemini Grounding für Optimierungsanalyse
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Gemini Grounding für Layer 5 Optimierungsanalyse aktiviert</li>
          <li>Quellen werden bei Ergebnissen angezeigt und nach Domain gruppiert</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Layer 3 Insights – Multi-Turn Conversation
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Insight-Generierung nutzt jetzt Multi-Turn-Conversations</li>
          <li>Positionsabhängige Follow-Up-Fragen an das KI-Modell</li>
          <li>Neuer Insights-Tab auf der Tools-Seite</li>
          <li>Admin-API für Insights-Anzeige und Zusammenfassung</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Layer 4 Insight Summarizer
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Zusammenfassung mehrerer Insights zu priorisierten Handlungsempfehlungen</li>
          <li>Ranking nach Häufigkeit: Hoch / Mittel / Niedrig</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Admin: Cheatsheet-Verwaltung
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Neuer Cheatsheet-Tab im Admin-Bereich für Layer 5 Optimierungs-Cheatsheet</li>
          <li>Versionierung mit Archiv- und Wiederherstellungsfunktion</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Admin: Autopilot-Tab & E-Mail-Benachrichtigungen
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Autopilot-Verwaltungstab mit Übersicht aller Autopiloten</li>
          <li>E-Mail-Benachrichtigungen bei Autopilot-Events</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          UI-Änderungen
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>"Benchmark" überall umbenannt zu "Optimierung"</li>
          <li>7T Mittelwert-Toggle im Trend-Chart</li>
          <li>URL-Eingabe akzeptiert jetzt auch Eingaben ohne https://</li>
          <li>Klickbare Prompts in der "Verwendete Prompts"-Sektion</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Debug Log API
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Debug Log API für externen Log-Zugriff (z.B. Claude Code, curl)</li>
          <li>API Key Management unter Admin → System → Debug API Keys</li>
          <li>In-Memory Log Buffer für schnellen Zugriff auf Backend-Logs</li>
          <li>API Key Authentifizierung mit SHA-256 Hash, IP-Whitelist und Ablaufdatum</li>
          <li>Key wird nur einmal angezeigt - sicher kopieren!</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Sicherheit & Bug Fixes
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>PII in Audit-Logs wird jetzt gehasht</li>
          <li>Health-Check um 6:50 Uhr mit Retry-Logik</li>
          <li>Partielle Job-Vervollständigung bei ungesunden Modellen</li>
          <li>Diverse Fixes für Custom Autopilot, Grounding Benchmark und Projekterstellung</li>
        </Typography>
      </Paper>

      {/* v4.8.5 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 4.8.5
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Score Heatmap Visualisierung
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Neue Heatmap-Ansicht
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Neue Heatmap-Visualisierung auf der Projektseite (Analyse-Tab)</li>
          <li>Zeilen = Prompts, Spalten = Tests/Tage</li>
          <li>Farbcodierung: Grün (80-100), Gelb (50-79), Orange (30-49), Rot (0-29)</li>
          <li>Hover zeigt den genauen Score-Wert</li>
          <li>Dropdown-Filter: "Alle Modelle" oder einzelnes Modell auswählen</li>
          <li>Prompt-Labels zeigen aufgelöste Platzhalter-Werte</li>
        </Typography>
      </Paper>

      {/* v4.8.4 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 4.8.4
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Tenant-spezifische LLM-Modell-Einschränkungen
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Modell-Konfiguration pro Mandant
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Admins können jetzt LLM-Modelle pro Mandant einschränken</li>
          <li>Zweite Filterebene zusätzlich zur globalen Modell-Aktivierung</li>
          <li>Konfiguration unter Admin &gt; Mandanten &gt; Mandant Bearbeiten</li>
          <li>Neue Sektion "Verfügbare KI-Modelle" mit Schaltern für jedes Modell</li>
          <li>Global deaktivierte Modelle sind als "Global deaktiviert" markiert</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Geschäftslogik
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Global INAKTIV = Für alle Mandanten nicht verfügbar</li>
          <li>Global AKTIV + Mandant AKTIV = Verfügbar</li>
          <li>Global AKTIV + Mandant INAKTIV = Für diesen Mandanten nicht verfügbar</li>
          <li>Admin-Benutzer sehen ALLE global aktiven Modelle</li>
          <li>Neue Mandanten haben standardmäßig alle Modelle aktiviert</li>
        </Typography>
      </Paper>

      {/* v4.8.3 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 4.8.3
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Scoring System Update & Bug Fixes
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Neues Punktesystem
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Position 1: 100 Punkte (unverändert)</li>
          <li>Position 2: 80 Punkte (vorher 75)</li>
          <li>Position 3: 60 Punkte (vorher 50)</li>
          <li>Position 4+: 30 Punkte (vorher 50)</li>
          <li>Nicht erwähnt: 0 Punkte (unverändert)</li>
          <li>Bestehende historische Scores bleiben unverändert</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Autopilot Fix für Admin-Benutzer
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Admin-Benutzer sehen jetzt Autopilot-Quota bei Mandanten-Projekten</li>
          <li>Admin-Benutzer sehen jetzt bestehende Autopilots für Mandanten-Projekte</li>
        </Typography>
      </Paper>

      {/* v4.8.2 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 4.8.2
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Bug Fixes & Improvements
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Layer 2 Model Konfiguration
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Layer-Konfiguration im Admin-Bereich funktioniert jetzt korrekt</li>
          <li>Änderungen am Layer 2 Modell werden sofort bei neuen Jobs angewendet</li>
          <li>Celery Worker liest Konfiguration jetzt aus der Datenbank statt aus Umgebungsvariablen</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Chatbot Token-Tracking
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Token-Nutzung wird jetzt korrekt für Mandanten-Benutzer erfasst</li>
          <li>Admin-Benutzer (ohne Mandant) verursachen keine Fehler mehr beim Chatbot</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          UI Verbesserungen
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Job-Logs zeigen Kosten jetzt mit 4 Dezimalstellen (vorher 3)</li>
        </Typography>
      </Paper>

      {/* v4.8.1 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 4.8.1
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Search Term Synonyms Feature
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Alternative Suchbegriffe
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Projekte können jetzt bis zu 2 alternative Suchbegriffe (Synonyme) haben</li>
          <li>Nützlich für Marken mit mehreren Namen (z.B. "Telefónica" / "O2" / "E-Plus")</li>
          <li>Synonyme sind optional - Hauptsuchbegriff bleibt erforderlich</li>
          <li>Alle Suchbegriffe werden bei der Analyse von KI-Antworten berücksichtigt</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Frontend
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Zwei optionale Synonym-Felder in der Projekterstellung (Schritt 3)</li>
          <li>Synonyme werden in der Projekt-Zusammenfassung angezeigt</li>
          <li>Projektdetails und Job-Details zeigen alle Suchbegriffe</li>
        </Typography>
      </Paper>

      {/* v4.8.0 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 4.8.0
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Admin Cost Bar Chart, Chatbot Docker Fix, Documentation Update
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Admin Kostenreports
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Neues Balkendiagramm für tägliche Kosten und Token-Nutzung</li>
          <li>Dual-Y-Achse: Kosten links, Tokens rechts</li>
          <li>Ergänzt das bestehende Liniendiagramm für Trend-Ansicht</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Chatbot Fix
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Chatbot funktioniert jetzt korrekt in Produktionsumgebung</li>
          <li>Ursache: chatbot_doc.md war nicht im Docker-Build enthalten</li>
          <li>Dokumentation wird jetzt korrekt im Container gefunden</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Dokumentation (chatbot_doc.md)
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Inline Prompt-Erstellung dokumentiert</li>
          <li>Support-Ticket-System dokumentiert</li>
          <li>Feature-Verfügbarkeits-Hinweise hinzugefügt</li>
          <li>Platzhalter-Syntax korrigiert auf [platzhalter]</li>
        </Typography>
      </Paper>

      {/* v4.7.4 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 4.7.4
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Admin Model Activation Control
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Model Active/Inactive Toggle
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Admins können LLM-Modelle in Einstellungen → KI-Modelle aktivieren/deaktivieren</li>
          <li>Toggle-Chips (Aktiv/Inaktiv) neben jedem Modell für Admin-Benutzer</li>
          <li>Inaktive Modelle werden Benutzern bei Projekterstellung nicht angezeigt</li>
          <li>Admins sehen alle Modelle, inaktive in rot mit "(Inaktiv)" Label</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          LLM-Preiskonfiguration (nur Admin)
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>"Preise konfigurieren" Button nur noch für Admin-Benutzer sichtbar</li>
          <li>Preisverwaltung auf Administratoren beschränkt</li>
        </Typography>
      </Paper>

      {/* v4.7.2 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 4.7.2
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          LLM Health Status Fix
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Bugfix
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>LLM-Gesundheitsstatus wird jetzt global und konsistent für alle Benutzer angezeigt</li>
          <li>Race Condition beim Laden der Modell-Health-Daten behoben</li>
        </Typography>
      </Paper>

      {/* v4.7.1 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 4.7.1
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Job Log & Per-Job Cost Tracking
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Admin Job Log
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Neuer Job Log Tab ersetzt Celery Worker Logs</li>
          <li>Zeigt abgeschlossene und fehlgeschlagene Jobs mit Zeitstempel</li>
          <li>Format: [DD.MM.YYYY HH:MM:SS] Job #123 abgeschlossen (Mandant, 2 Prompts, 2 Modelle, LLM Score: 85.5) 0.057$</li>
          <li>Farbcodiert: Grün für abgeschlossen, Rot für fehlgeschlagen</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Job-Level Kostentracking
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Token-Nutzung jetzt direkt mit Jobs verknüpft</li>
          <li>Genaue Kostenberechnung pro Job im Job Log</li>
          <li>Neue job_id Spalte in token_usage Tabelle</li>
        </Typography>
      </Paper>

      {/* v4.7.0 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 4.7.0
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Admin Dashboard Consolidation & UX Improvements
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Unified Admin Dashboard
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Alle Admin-Tabs von Einstellungen ins Admin Dashboard verschoben</li>
          <li>Admin Dashboard mit 10 Tabs: Dashboard, Mandanten, Nutzer, Kostenreports, Tokens, Layers, System, Changelog, Queue, Logs</li>
          <li>Einstellungen vereinfacht auf 2 Tabs: Prompts, KI-Modelle (für alle Benutzer)</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Erweitertes Token-Tracking
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Tokens-Tab zeigt jetzt alle Verarbeitungsschichten (Layer 1, 2, 2.1, 4, 5)</li>
          <li>Chatbot-Token-Nutzung hinzugefügt</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Mandantenverwaltung
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Neue "Quota"-Spalte zeigt Autopilot-Prompt-Nutzung (z.B. "2/5")</li>
          <li>Benutzer-Löschung funktioniert jetzt korrekt</li>
        </Typography>
      </Paper>

      {/* v4.6.0 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 4.6.0
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          PII Encryption at Rest
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          PII-Verschlüsselung
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>E-Mail-Adressen werden mit Fernet (AES-128-CBC) verschlüsselt gespeichert</li>
          <li>Benutzernamen werden ebenfalls verschlüsselt</li>
          <li>HKDF-Schlüsselableitung für separate Verschlüsselungs- und Index-Schlüssel</li>
          <li>Blind Index (HMAC-SHA256) ermöglicht schnelle E-Mail-Suche ohne Entschlüsselung</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Sicherheitsvorteile
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Datenbank-Breach legt keine Klartextdaten offen</li>
          <li>Backup-Dateien enthalten nur verschlüsselte PII</li>
          <li>Login funktioniert weiterhin via Blind Index (keine Performance-Einbußen)</li>
          <li>Vorbereitet für Schlüsselrotation</li>
        </Typography>
      </Paper>

      {/* v4.5.0 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 4.5.0
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Security Hardening Release
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Token/Session-Sicherheit
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Kurzlebige Access-Tokens (15 Minuten) mit HttpOnly-Cookie Refresh-Tokens</li>
          <li>JWT aus localStorage entfernt - nur noch In-Memory-Speicherung</li>
          <li>Session-Token werden mit SHA-256 gehasht in der Datenbank gespeichert</li>
          <li>Token-Rotation bei jeder Refresh-Anfrage</li>
        </Typography>
      </Paper>

      {/* v4.4.0 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 4.4.0
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Support Ticket System
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          User Support Form
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Neuer "Support" Button in der Sidebar über dem Logout-Button</li>
          <li>Einfaches Feedback-Formular mit Titel, Nachricht und optionalem Anhang</li>
          <li>Unterstützt Bilder, PDF, Word und Textdateien (max. 5MB)</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Admin Support Inbox
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Ungelesene-Badge: Mobile-App-Stil Benachrichtigungsanzeige am Support-Button</li>
          <li>Ticket-Listenansicht mit Tabs für offene und gelöste Tickets</li>
          <li>Detailansicht mit Absender, Datum, Nachricht und Anhang-Download</li>
        </Typography>
      </Paper>

      {/* v4.2.0 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 4.2.0
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Layer 2.1: Champion Data Consolidation & Queue Management
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Champion Data Consolidation (Layer 2.1)
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Champion Name Tracking: Neues champion_name Feld in JobResults</li>
          <li>ChampionConsolidator Service: Intelligente Deduplizierung von Champion-Namen</li>
          <li>Multi-Stage Matching: Substring-Erkennung, Abkürzungs-Handling, LLM-Verifizierung</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Admin: Queue Management
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Neuer Queue-Tab in Einstellungen (nur für Admins)</li>
          <li>Anzeige aller wartenden und laufenden LLM Score Jobs</li>
          <li>Anzeige aller wartenden und laufenden Optimierungs-Tests</li>
        </Typography>
      </Paper>

      {/* v4.1.0 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 4.1.0
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Autopilot - Automated Testing & Quota Management
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Autopilot Features
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>LLM Score Autopilot: Automatische tägliche LLM-Score-Tests (täglich um 7:00 Uhr)</li>
          <li>Optimierungs-Autopilot: Automatische wöchentliche Optimierungsanalysen</li>
          <li>Vollständiges CRUD: Erstellen, Bearbeiten, Pausieren, Fortsetzen und Löschen</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Admin: Quota Management
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Tenant-Level Prompt-Quotas zur Kostenkontrolle</li>
          <li>Maximale Autopilot-Prompts pro Tenant konfigurierbar (Standard: 10)</li>
          <li>Anzeige der aktuellen Quota-Nutzung und verfügbaren Prompts</li>
        </Typography>
      </Paper>

      {/* v4.0.0 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Version 4.0.0
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Multi-Tenant Architecture & Enterprise Features
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Multi-Tenancy
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Vollständige Multi-Tenant-Architektur mit Daten-Isolation</li>
          <li>Tenant-Entity mit Namen, Anzeigenamen und Aktiv-Status</li>
          <li>tenant_id Foreign Key zu allen bestehenden Tabellen hinzugefügt</li>
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Authentication & Authorization
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>JWT-basiertes Authentifizierungssystem mit sicherer Login/Logout</li>
          <li>Email und Passwort-basierter Login mit bcrypt-Hashing (Faktor 12)</li>
          <li>Server-seitige Session-Verwaltung mit automatischem Ablauf</li>
        </Typography>
      </Paper>

      {/* v3.0.0 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
          Version 3.0.0
        </Typography>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Optimierungsanalyse & Korrelationssystem
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
          Major Features
        </Typography>
        <Typography component="ul" sx={{ ml: 2 }}>
          <li>Optimierungsanalyse mit 6 Kriterien (KI-gestützt)</li>
          <li>Combined Timeline Chart: Korrelation zwischen LLM-Rankings und Optimierungs-Scores</li>
          <li>Pearson-Korrelation: Statistische Analyse (r-Wert)</li>
          <li>Homepage-URL-Management pro Projekt</li>
        </Typography>
      </Paper>

      {/* v2.4.0 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
          Version 2.4.0
        </Typography>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          AI-Powered Chatbot Integration
        </Typography>

        <Typography component="ul" sx={{ ml: 2 }}>
          <li>KI-Chatbot-Assistent mit Gemini Flash 2.5</li>
          <li>Umfassende deutsche Dokumentation als Wissensbasis</li>
          <li>Animierte Tipp-Indikatoren</li>
          <li>Nachrichtenverlauf mit Zeitstempeln</li>
        </Typography>
      </Paper>

      <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">
          Vollständige Release-Notes finden Sie auf{' '}
          {/* Repo slug, not the product name — update when the GitHub repo is renamed. */}
          <a href="https://github.com/ab430/SAIO/releases" target="_blank" rel="noopener noreferrer">
            GitHub
          </a>
        </Typography>
      </Box>
    </Box>
  );
};

export default ChangelogTab;
